/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Hidden,
  BottomNavigation,
  BottomNavigationAction,
  useTheme,
} from '@material-ui/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
// import { toast } from 'react-toastify';
import { endOfYear, format, startOfYear } from 'date-fns';
import { FaHome, FaFilter, FaCalendarAlt } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import api from '../../services/api';

import YearSelector from '../../components/selectors/YearSelector';
import FilterByYear from '../../components/utils/FilterByYear';
import CardButton from './CardButton';
import WeekSchedule from './WeekSchedule';
import DimensioningChart from './DimensioningChart';
import StaffDonutChart from './StaffDonutChart';

import {
  calculateAverage,
  getTotalDaysPerWeek,
  transformWeeksToMonths,
} from '../../utils/helper';
import { useAuth } from '../../hooks/auth';

import { useStyles } from './styles';
import {
  IEquipmentsData,
  IEquipmentsReturn,
  IEquipmentV2,
  IForeseeV2Data,
  IForeseeV2Return,
  IWorkcenter,
} from '../../types/kpis';
import { usePreferences } from '../../hooks/preferences';
import CardSkeleton from '../../components/utils/Skeletons/CardSkeleton';
import ChartSkeleton from '../../components/utils/Skeletons/ChartSkeleton';
import TableSkeleton from '../../components/utils/Skeletons/TableSkeleton';
import FilterModal from '../../components/utils/FilterModal';
import FiltersList from '../../components/utils/FiltersList';

const EQUIPMENTS_INITIAL_STATE: IEquipmentsData = {
  average: {
    workcenters: [],
    annual: [],
  },
  equipments: [],
};

const Dimensioning: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const { jwt } = useAuth();
  const history = useHistory();
  const { disableTabs, selectDataType } = usePreferences();
  const [currentYear, setCurrentYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [selectedCategory, setSelectedCategory] = useState<any>('annual');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [temporaryFilters, setTemporaryFilters] = useState<string[]>([]);
  const [foreseeV2Data, setForeseeV2Data] = useState<IForeseeV2Data | null>(
    null,
  );
  const [equipmentsData, setEquipmentsData] = useState<IEquipmentsData>(
    EQUIPMENTS_INITIAL_STATE,
  );
  const [annualData, setAnnualData] = useState<number[]>([]);
  const [workcentersData, setWorkcentersData] = useState<IWorkcenter[]>([]);
  const [filteredData, setFilteredData] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filteredEquipments, setFilteredEquipments] = useState<IEquipmentV2[]>(
    [],
  );
  const [filteredAnnual, setFilteredAnnual] = useState<number[]>([]);
  const [openFilterByYearModal, setOpenFilterByYearModal] = useState(false);

  const getForeseeV2Data = useCallback(
    async (yr: number): Promise<void> => {
      const startDate = format(startOfYear(new Date(yr, 1, 1)), 'yyyy-MM-dd');
      const endDate = format(endOfYear(new Date(yr, 1, 1)), 'yyyy-MM-dd');
      try {
        const res = await api.get<IForeseeV2Return>(
          `effective_sizing/foresee/v2/${startDate}/${endDate}`,
          {
            headers: {
              'x-access-token': jwt,
            },
          },
        );
        setForeseeV2Data(res.data.data);
      } catch {
        // toast.error(t('screens:dimensioning.unableToLoadTotalStaff'));
      }
    },
    [jwt],
  );

  const getEquipmentsData = useCallback(
    async (yr: number): Promise<void> => {
      const startDate = format(startOfYear(new Date(yr, 1, 1)), 'yyyy-MM-dd');
      const endDate = format(endOfYear(new Date(yr, 1, 1)), 'yyyy-MM-dd');
      try {
        const res = await api.get<IEquipmentsReturn>(
          `effective_sizing/equipments/${startDate}/${endDate}`,
          {
            headers: {
              'x-access-token': jwt,
            },
          },
        );
        setEquipmentsData(res.data.data);
        setAnnualData(res.data.data.average.annual);
        setWorkcentersData(res.data.data.average.workcenters);
        setFilteredData(res.data.data.average.annual);
      } catch {
        // toast.error(t('screens:dimensioning.unableToLoadTotalStaff'));
      }
    },
    [jwt],
  );

  const handleFilters = (name: string, active: boolean): void => {
    const index = temporaryFilters.findIndex(ft => ft === name);
    if (index === -1 && active) {
      const newFilters = [...temporaryFilters, name];
      setTemporaryFilters(newFilters);
      return;
    }

    if (!active) {
      const newFilters = [...temporaryFilters];
      newFilters.splice(index, 1);
      setTemporaryFilters(newFilters);
    }
  };

  const handleToggleFilter = (): void => {
    if (filteredEquipments.length > 0) {
      setFilteredEquipments([]);
      setFilteredAnnual([]);
      return;
    }
    setShowFilterModal(!showFilterModal);
  };

  const input = getTotalDaysPerWeek();
  const labels = useMemo(() => {
    const lbs = input.map(dt => {
      const localeLabelStr: any = `dates:months.${dt.label.toLowerCase()}`;
      return t(localeLabelStr).substring(0, 3);
    });
    return lbs;
  }, [input, t]);

  const filterLabel = useMemo(() => {
    if (filteredEquipments.length > 0) {
      return t('components:buttons.clear');
    }
    return t('components:buttons.filter');
  }, [filteredEquipments, t]);

  useEffect(() => {
    getEquipmentsData(currentYear);
    getForeseeV2Data(currentYear);
  }, [currentYear, jwt, t, getForeseeV2Data, getEquipmentsData]);

  useEffect(() => {
    selectDataType('drills');
    disableTabs({
      roads: true,
      trucks: true,
      drills: false,
    });
  }, [selectDataType, disableTabs]);

  useEffect(() => {
    if (foreseeV2Data && annualData.length > 0 && workcentersData.length > 0) {
      setLoading(false);
    }
  }, [foreseeV2Data, annualData, workcentersData]);

  return (
    <div className={classes.container}>
      <FiltersList
        loading={loading}
        filterLabel={filterLabel}
        type="effective_sizing"
        data={
          filteredEquipments.length > 0
            ? filteredEquipments
            : equipmentsData.equipments
        }
        activeFilters={activeFilters}
        temporaryFilters={temporaryFilters}
        onChange={() => handleToggleFilter()}
        onChangeFilter={(name, active) => handleFilters(name, active)}
      />
      <div className={classes.main}>
        <div className={classes.staffDimensioning}>
          <div className={classes.titleContainer}>
            <Typography variant="h2" className={classes.title}>
              {t('screens:dimensioning.totalStaffDimensioning')}
            </Typography>
            <Hidden smDown>
              <YearSelector
                value={currentYear}
                onChange={y => setCurrentYear(y)}
              />
            </Hidden>
          </div>

          <div className={classes.cardsContainer}>
            {loading
              ? null
              : annualData.length > 0 && (
                  <CardButton
                    title={t('screens:dimensioning.totalStaff')}
                    value={
                      filteredAnnual.length > 0
                        ? Math.ceil(calculateAverage(filteredAnnual, false))
                        : Math.ceil(calculateAverage(annualData, false))
                    }
                    isSelected={selectedCategory === 'annual'}
                    onClick={() => setSelectedCategory('annual')}
                  />
                )}
            {loading
              ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(val => (
                  <CardSkeleton key={`card_${val}`} />
                ))
              : filteredEquipments.length > 0
              ? filteredEquipments.map(ft => {
                  const allWk = ft.workcenters.map(wk => wk);
                  return allWk.map(dt => (
                    <CardButton
                      key={`${dt.name}_${ft.id}`}
                      title={dt.name}
                      value={Math.ceil(calculateAverage(dt.values, false))}
                      isSelected={selectedCategory === dt.name}
                      onClick={() => setSelectedCategory(dt.name)}
                    />
                  ));
                })
              : workcentersData.length > 0 &&
                workcentersData.map(wc => {
                  return (
                    <CardButton
                      key={wc.name}
                      title={wc.name}
                      value={Math.ceil(calculateAverage(wc.values, false))}
                      isSelected={selectedCategory === wc.name}
                      onClick={() => setSelectedCategory(wc.name)}
                    />
                  );
                })}
          </div>
        </div>
        <div className={classes.chartAndTableContainer}>
          <div className={classes.chartContainer}>
            {loading ? (
              <ChartSkeleton height={200} width="100%" />
            ) : (
              foreseeV2Data &&
              foreseeV2Data.historical.length > 0 &&
              foreseeV2Data.executed.length > 0 &&
              foreseeV2Data.ideal.length > 0 && (
                <DimensioningChart
                  labels={labels}
                  year={currentYear}
                  historical={transformWeeksToMonths(
                    foreseeV2Data.historical,
                    currentYear,
                  )}
                  executed={transformWeeksToMonths(
                    foreseeV2Data.executed,
                    currentYear,
                  )}
                  ideal={transformWeeksToMonths(
                    foreseeV2Data.ideal,
                    currentYear,
                  )}
                />
              )
            )}
          </div>

          <Hidden mdUp>
            <div className={classes.divider}>
              <Divider />
            </div>
          </Hidden>

          <div className={classes.tableContainer}>
            <div className={classes.tableSubtitle}>
              <Typography variant="subtitle1" className={classes.subtitle}>
                {t('screens:dimensioning.jobDemand')}
              </Typography>
            </div>
            {loading ? (
              <TableSkeleton />
            ) : (
              <Table size="small">
                <TableBody>
                  {foreseeV2Data &&
                    foreseeV2Data.demand &&
                    foreseeV2Data.demand.length > 0 &&
                    foreseeV2Data.demand.map(demand => (
                      <TableRow key={`demand_table_${demand.name}`}>
                        <TableCell align="left" className={classes.cell}>
                          {demand.name}
                        </TableCell>
                        <TableCell align="center" className={classes.cell}>
                          {Math.ceil(calculateAverage(demand.values))}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        <Divider />

        <div className={classes.bottomContentContainer}>
          <div
            className={clsx(classes.scheduleContainer, {
              [classes.loadingMargin]: loading,
            })}
          >
            <div className={classes.titleContainer}>
              <Typography variant="h2" className={classes.title}>
                {t('screens:dimensioning.weeklyStaffDistribution')}
              </Typography>
            </div>
            {loading ? (
              <ChartSkeleton height={300} width="100%" />
            ) : (
              filteredData.length > 0 && (
                <WeekSchedule
                  data={filteredData}
                  category={selectedCategory}
                  year={currentYear}
                />
              )
            )}
          </div>
          <div className={classes.donutContainer}>
            <div
              className={classes.titleContainer}
              style={{ justifyContent: 'center' }}
            >
              <Typography
                variant="h2"
                className={classes.title}
                style={{ textAlign: 'center' }}
              >
                {t('screens:dimensioning.staffByMaintenanceType')}
              </Typography>
            </div>
            {loading ? (
              <ChartSkeleton height={300} width="100%" />
            ) : (
              <StaffDonutChart
                data={
                  foreseeV2Data &&
                  foreseeV2Data.corrective &&
                  foreseeV2Data.corrective.length > 0
                    ? [
                        Math.ceil(
                          calculateAverage(foreseeV2Data.corrective, false),
                        ),
                        Math.ceil(
                          calculateAverage(foreseeV2Data.conditional, false),
                        ),
                        Math.ceil(
                          calculateAverage(foreseeV2Data.systematic, false),
                        ),
                      ]
                    : [0, 0, 0]
                }
              />
            )}
            <FilterModal
              workcenters={workcentersData}
              equipments={equipmentsData}
              open={showFilterModal}
              onClose={() => setShowFilterModal(false)}
              onDone={() => setShowFilterModal(false)}
              filtered={equips => setFilteredEquipments(equips)}
              year={currentYear}
              onAnnualChange={newAnnual => setFilteredAnnual(newAnnual)}
            />

            <FilterByYear
              min={2015}
              max={new Date().getFullYear()}
              open={openFilterByYearModal}
              selectedYear={currentYear}
              onClose={() => setOpenFilterByYearModal(false)}
              onChangeYear={year => setCurrentYear(year)}
            />
          </div>
        </div>
      </div>

      {/* Bottom tab bar, only on xs and sm screens */}
      <Hidden mdUp>
        <BottomNavigation showLabels className={classes.bottomNavigation}>
          <BottomNavigationAction
            className={classes.bottomNavLabel}
            label={t('components:nav.home')}
            onClick={() => history.push('/dashboard')}
            icon={<FaHome size={24} color={theme.palette.common.white} />}
          />
          <BottomNavigationAction
            className={classes.bottomNavLabel}
            label={
              filteredEquipments.length > 0
                ? filterLabel
                : t('screens:results.filterByDrill')
            }
            onClick={() => handleToggleFilter()}
            icon={<FaFilter size={24} color={theme.palette.common.white} />}
            style={{
              borderStyle: 'solid',
              borderLeftWidth: 1,
              borderColor: '#FFF',
            }}
          />
          <BottomNavigationAction
            className={classes.bottomNavLabel}
            label={t('screens:results.filterByYear')}
            onClick={() => setOpenFilterByYearModal(true)}
            icon={
              <FaCalendarAlt size={24} color={theme.palette.common.white} />
            }
            style={{
              borderStyle: 'solid',
              borderLeftWidth: 1,
              borderColor: '#FFF',
            }}
          />
        </BottomNavigation>
      </Hidden>
    </div>
  );
};

export default Dimensioning;
